// frontend/src/pages/StudentProfile.js
import { useEffect, useState } from "react";
import { BACKEND_URL } from "../config";
import { safeGetItem, safeGetJSON } from "../utils/storage";
import { useAuth } from "../hooks/useAuth";

const toast = (message, type = "success") => {
  const colors = {
    success: "bg-emerald-500/90 text-white",
    error: "bg-rose-500/90 text-white",
  };
  const container = document.createElement("div");
  container.className = `fixed top-6 right-6 z-50 px-4 py-3 rounded-xl shadow-lg animate-fade-in ${colors[type]}`;
  container.textContent = message;
  document.body.appendChild(container);
  setTimeout(() => {
    container.classList.add("animate-fade-out");
    setTimeout(() => container.remove(), 300);
  }, 1800);
};

export default function StudentProfile() {
  const { user: authUser, token } = useAuth();
  const student = authUser || safeGetJSON("user");
  const email = student?.email || safeGetItem("studentEmail");

  const [name, setName] = useState(student?.name || "");
  const [phone, setPhone] = useState(student?.phone || "");
  const [preferences, setPreferences] = useState({
    interestedCountries: [],
    preferredCourses: [],
    budget: "",
    studyPlan: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);

  useEffect(() => {
    async function loadPrefs() {
      if (!email) return;
      try {
        const res = await fetch(`${BACKEND_URL}/student/preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setPreferences({
            interestedCountries: data.interestedCountries || [],
            preferredCourses: data.preferredCourses || [],
            budget: data.budget || "",
            studyPlan: data.studyPlan || "",
          });
        }
      } catch (err) {
        console.error("Profile load error", err);
      }
    }
    loadPrefs();
  }, [email, token]);

  const saveProfile = async () => {
    if (!name.trim()) return toast("Please add your full name", "error");
    setSavingProfile(true);
    try {
      const res = await fetch(`${BACKEND_URL}/student/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, name, phone }),
      });
      if (!res.ok) throw new Error();
      toast("Profile updated");
    } catch (err) {
      toast("Unable to save profile", "error");
      console.error(err);
    } finally {
      setSavingProfile(false);
    }
  };

  const savePreferences = async () => {
    setSavingPreferences(true);
    try {
      const res = await fetch(`${BACKEND_URL}/student/preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, ...preferences }),
      });
      if (!res.ok) throw new Error();
      toast("Preferences saved");
    } catch (err) {
      toast("Could not save preferences", "error");
      console.error(err);
    } finally {
      setSavingPreferences(false);
    }
  };

  if (!email)
    return (
      <div className="min-h-screen flex items-center justify-center text-lg text-slate-700">
        Please login first.
      </div>
    );

  const renderInput = (label, value, onChange, options = {}) => (
    <label className="block space-y-2">
      <span className="text-sm font-medium text-gray-600">{label}</span>
      <input
        type="text"
        className={`w-full rounded-xl border border-gray-200 bg-white/80 px-4 py-3 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition ${
          options.readOnly ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
        }`}
        placeholder={options.placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={options.readOnly}
      />
    </label>
  );

  const renderTextArea = (label, value, onChange, placeholder) => (
    <label className="block space-y-2">
      <span className="text-sm font-medium text-gray-600">{label}</span>
      <textarea
        className="w-full rounded-xl border border-gray-200 bg-white/80 px-4 py-3 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition min-h-[120px]"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-8 text-white sm:px-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[4px] text-cyan-200">Student Profile</p>
          <h1 className="mt-3 text-3xl font-semibold">Profile and preferences</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
            Keep your personal details and study preferences up to date so consultants can reach you quicker.
          </p>
        </div>

        <section className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-5">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h2 className="text-lg font-semibold text-white">Personal Information</h2>
              <span className="text-xs uppercase tracking-[3px] text-slate-500">
                Required
              </span>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                {renderInput("Full Name", name, setName, {
                  placeholder: "e.g. Priya Reddy",
                })}
              </div>
              {renderInput("Email address", email, () => {}, {
                readOnly: true,
                placeholder: "student@example.com",
              })}
              {renderInput("Phone number", phone, setPhone, {
                placeholder: "+91 98XXXXXX12",
              })}
            </div>
            <button
              onClick={saveProfile}
              className="mt-6 w-full rounded-xl bg-cyan-300 py-3 font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:opacity-60"
              disabled={savingProfile}
            >
              {savingProfile ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Saving profile...
                </span>
              ) : (
                "Save Profile"
              )}
            </button>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-5">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h2 className="text-lg font-semibold text-white">Study Preferences</h2>
              <span className="text-xs uppercase tracking-[3px] text-slate-500">
                Tell us more
              </span>
            </div>
            <div className="mt-6 space-y-4">
              {renderInput(
                "Preferred Countries",
                preferences.interestedCountries.join(", "),
                (val) =>
                  setPreferences((prev) => ({
                    ...prev,
                    interestedCountries: val
                      .split(",")
                      .map((entry) => entry.trim())
                      .filter(Boolean),
                  })),
                { placeholder: "UK, USA, Canada..." }
              )}
              {renderInput(
                "Preferred Courses",
                preferences.preferredCourses.join(", "),
                (val) =>
                  setPreferences((prev) => ({
                    ...prev,
                    preferredCourses: val
                      .split(",")
                      .map((entry) => entry.trim())
                      .filter(Boolean),
                  })),
                { placeholder: "MBA, MS, UX Design..." }
              )}
              {renderInput(
                "Budget (INR)",
                preferences.budget,
                (val) =>
                  setPreferences((prev) => ({ ...prev, budget: val })),
                { placeholder: "15–20 Lacs" }
              )}
              {renderTextArea(
                "Planned Intake (Year / Season)",
                preferences.studyPlan,
                (val) =>
                  setPreferences((prev) => ({ ...prev, studyPlan: val })),
                "Fall 2024, Spring 2025..."
              )}
            </div>
            <button
              onClick={savePreferences}
              className="mt-6 w-full rounded-xl bg-emerald-300 py-3 font-semibold text-slate-950 transition hover:bg-emerald-200 disabled:opacity-60"
              disabled={savingPreferences}
            >
              {savingPreferences ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Saving preferences...
                </span>
              ) : (
                "Save Preferences"
              )}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
